import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { Button } from './ui/button';
import { EmptyState } from './ui/empty-state';

const MAX = 2000;

function getInitials(email: string): string {
  // e.g. "shared@kaopiz.local" → "SK", "john.doe@example.com" → "JD"
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._-]/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return ((parts[0][0] ?? '') + (parts[1][0] ?? '')).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

export function CommentsPanel({ transactionId }: { transactionId: number }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['comments', transactionId],
    queryFn: () => api.listComments(transactionId),
  });
  const [draft, setDraft] = useState('');
  const create = useMutation({
    mutationFn: (body_text: string) => api.createComment(transactionId, body_text),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comments', transactionId] });
      setDraft('');
    },
  });

  const trimmed = draft.trim();
  const overLimit = trimmed.length > MAX;
  const canSubmit = trimmed.length >= 1 && !overLimit && !create.isPending;

  return (
    <div className="mt-4 rounded-lg border border-border bg-card p-3">
      <div className="mb-3 text-sm font-medium text-foreground">Comments</div>
      {isLoading && <div className="text-xs text-muted-foreground/70">Loading…</div>}
      {data && data.comments.length === 0 && (
        <EmptyState
          icon={MessageSquare}
          title="No comments yet"
          description="Be the first to add a note."
        />
      )}
      {data && data.comments.length > 0 && (
        <ul className="mb-3 space-y-2">
          {data.comments.map((c) => (
            <li key={c.id} className="flex gap-2 rounded bg-background p-2 text-sm">
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
                title={c.actor_email}
              >
                {getInitials(c.actor_email)}
              </div>
              <div className="flex-1">
                <div className="mb-1 text-xs text-muted-foreground">
                  {c.actor_email} · {formatDateTime(c.created_at)}
                </div>
                <div className="whitespace-pre-line text-foreground">{c.body_text}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note (Markdown not rendered)…"
          className="w-full rounded border border-border bg-background p-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          rows={3}
        />
        <div className="mt-1 flex items-center justify-between text-xs">
          <span className={overLimit ? 'text-destructive' : 'text-muted-foreground/70'}>
            {trimmed.length}/{MAX}
          </span>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => create.mutate(trimmed)}
            disabled={!canSubmit}
          >
            Add comment
          </Button>
        </div>
      </div>
    </div>
  );
}
